import * as React from 'react';
import { useEffect, useState } from 'react';

import { QUESTION_IDS, type QUESTION_IDS_TYPE } from 'bundles/onboarding-common/constants/questionScheme';
import OnboardingOptionsContext from 'bundles/onboarding-common/contexts/OnboardingOptionsContext';
import OnboardingProgressContext from 'bundles/onboarding-common/contexts/OnboardingProgressContext';
import OnboardingQuestionDataContext from 'bundles/onboarding-common/contexts/OnboardingQuestionDataContext';
import useFetchData from 'bundles/onboarding-common/hooks/useFetchData';
import useGetAllSkills from 'bundles/onboarding-common/hooks/useGetAllSkills';
import useLearningGoalData from 'bundles/onboarding-common/hooks/useLearningGoalData';
import type { QuestionValue, SubmitAnswerOptions } from 'bundles/onboarding-common/types';
import { isQuestionAnswered } from 'bundles/onboarding-common/utils';

type Props = {
  /**
   * The list of question ids that are fetched from the BFF
   */
  questionIds: QUESTION_IDS_TYPE[];
  /**
   * The children of the provider
   */
  children: React.ReactNode;
};

const ModularOnboardingProvider = ({ questionIds, children }: Props) => {
  // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  const [questionId, setQuestionId] = useState<QUESTION_IDS_TYPE | null>(questionIds[0]);
  const [currentStep, setCurrentStep] = useState(1);
  const [questionValue, setQuestionValue] = useState<QuestionValue | null | undefined>(null);
  const [hasAnsweredQuestion, setHasAnsweredQuestion] = useState(false);
  const [validationErrorMessage, setValidationErrorMessage] = useState<string | undefined>(undefined);
  const [submitAnswerOptions, setSubmitAnswerOptions] = useState<SubmitAnswerOptions | undefined>(undefined);
  const [submitAnswerErrorMessage, setSubmitAnswerErrorMessage] = useState<string | undefined>(undefined);

  const { value: learningGoal } = useFetchData({
    useQueryData: useLearningGoalData,
    questionIdForLocalStorage: QUESTION_IDS.learningGoal,
    skip: ![QUESTION_IDS.learningGoal, QUESTION_IDS.roleSelect].includes(questionId || ''),
  });
  const {
    allSkills,
    loading: isLoadingAllSkills,
    type: skillsType,
  } = useGetAllSkills({ skip: questionId !== QUESTION_IDS.skillSelect });

  const onChange = (value: QuestionValue) => {
    setQuestionValue(value);
  };

  const resetQuestionData = () => {
    setQuestionValue(null);
    setHasAnsweredQuestion(false);
    setValidationErrorMessage(undefined);
    setSubmitAnswerOptions(undefined);
  };

  const totalSteps = questionIds.length;

  useEffect(() => {
    if (questionId) {
      setCurrentStep(questionIds.indexOf(questionId) + 1);
    }
  }, [questionId, questionIds]);

  useEffect(() => {
    setHasAnsweredQuestion(isQuestionAnswered(questionValue));
  }, [questionValue]);

  return (
    <OnboardingProgressContext.Provider value={{ currentStep, questionId, questionIds, totalSteps, setQuestionId }}>
      <OnboardingQuestionDataContext.Provider
        value={{
          questionValue,
          hasAnsweredQuestion,
          validationErrorMessage,
          submitAnswerOptions,
          submitAnswerErrorMessage,
          onChange,
          setQuestionValue,
          setValidationErrorMessage,
          resetQuestionData,
          setHasAnsweredQuestion,
          setSubmitAnswerOptions,
          setSubmitAnswerErrorMessage,
        }}
      >
        <OnboardingOptionsContext.Provider
          value={{ learningGoal, allSkills, isLoadingSkills: isLoadingAllSkills, skillsType }}
        >
          {children}
        </OnboardingOptionsContext.Provider>
      </OnboardingQuestionDataContext.Provider>
    </OnboardingProgressContext.Provider>
  );
};

export default ModularOnboardingProvider;
